import React from "react";
import "./About.css";
import Banner from "../Banner/Banner";

const About = () => {
  return (
    <section className="aboutv2">
      <div className="av2-wrap">
        {/* Intro */}
        <div className="av2-head">
          <p className="av2-kicker">About us</p>
          <h1 className="av2-title">
            A small team focused on building websites that actually grow your business.
          </h1>
          <p className="av2-sub">
            We design, build, and optimize fast, reliable websites that turn visitors into
            customers—combining clear strategy, thoughtful design, and clean engineering.
          </p>
        </div>

        {/* Hero split */}
        <div className="av2-hero">
          <div className="av2-hero-text">
            <p>
              Good design is not just how a website looks—it’s how it performs. We care about
              load speed, clarity, and the tiny details that make people trust your brand.
              From information architecture to final deployment, everything is aligned with
              your business goals.
            </p>
            <ul className="av2-points" role="list">
              <li>
                <span className="dot" />
                Conversion-focused layouts and UX
              </li>
              <li>
                <span className="dot" />
                Clean, maintainable code and best practices
              </li>
              <li>
                <span className="dot" />
                Analytics, tracking, and optimization baked in
              </li>
            </ul>
          </div>

          <div className="av2-hero-media">
            <div className="av2-hero-meta">
              <h3>What you can expect</h3>
              <ul>
                <li>Simple communication, no jargon</li>
                <li>Clear timelines and milestones</li>
                <li>Pixel-perfect builds from Figma or scratch</li>
                <li>Support after launch, not just hand-off</li>
              </ul>
            </div>

            <div className="av2-badge">
              <strong>Focus</strong> • Strategy → Design → Build → Refine
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="av2-block">
          <h2 className="av2-h2">What we care about</h2>
          <p className="av2-lead">
            A few principles that shape how we work and how your projects are delivered.
          </p>

          <div className="av2-values">
            {[
              {
                title: "Clarity over complexity",
                text: "We keep things simple for users and clients—clear flows, clear communication, and no unnecessary features.",
                icon: "🧭",
              },
              {
                title: "Quality in the details",
                text: "From typography to micro-interactions, we obsess over the small details that build trust.",
                icon: "✨",
              },
              {
                title: "Performance first",
                text: "Fast, responsive, and technically sound builds that don’t break under real-world traffic.",
                icon: "⚡",
              },
              {
                title: "Long-term partnerships",
                text: "We don’t vanish after launch. We stay to refine, iterate, and grow with you.",
                icon: "🤝",
              },
            ].map((v, i) => (
              <div key={i} className="value-card">
                <div className="value-icon" aria-hidden="true">
                  {v.icon}
                </div>
                <h3>{v.title}</h3>
                <p>{v.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="av2-stats">
          {[
            { k: "40+ ", d: "Websites & landing pages delivered" },
            { k: "15+ ", d: "Industries worked with" },
            { k: "4.9/5", d: "Average client satisfaction" },
            { k: "72 hrs", d: "Typical turnaround for small fixes" },
          ].map((s, i) => (
            <div key={i} className="stat">
              <div className="k">{s.k}</div>
              <div className="d">{s.d}</div>
            </div>
          ))}
        </div>

        {/* Process / Timeline */}
        <div className="av2-block">
          <h2 className="av2-h2">How we work</h2>
          <p className="av2-lead">
            A straightforward, repeatable process that keeps projects on time and on track.
          </p>

          <ol className="av2-steps">
            <li>
              <div className="step-pin" aria-hidden="true" />
              <h3>Listen & understand</h3>
              <p>
                We start with your goals, your users, and what’s not working right now. No templates,
                just real context.
              </p>
            </li>
            <li>
              <div className="step-pin" aria-hidden="true" />
              <h3>Plan & prototype</h3>
              <p>
                We organize content, define the structure, and create wireframes or mockups so you
                can see the plan before we build.
              </p>
            </li>
            <li>
              <div className="step-pin" aria-hidden="true" />
              <h3>Build & launch</h3>
              <p>
                We develop, test, and refine your site across modern devices and browsers, then
                handle a smooth launch.
              </p>
            </li>
            <li>
              <div className="step-pin" aria-hidden="true" />
              <h3>Support & improve</h3>
              <p>
                After launch, we monitor, tweak, and optimize based on real user behavior and
                performance data.
              </p>
            </li>
          </ol>
        </div>

        {/* Quote */}
        <figure className="av2-quote">
          <blockquote>
            “They didn’t just rebuild our website—they helped us rethink how we present our services
            online. Enquiries went up, and clients now understand what we do much faster.”
          </blockquote>
          <figcaption>— Client feedback</figcaption>
        </figure>

        {/* CTA grid */}
        <div className="av2-cta">
          <div className="cta-card">
            <h3>Need help with a website or landing page?</h3>
            <p>
              Share where you are right now and what you want to achieve—we’ll propose a simple,
              phased plan.
            </p>
            <a href="/services" className="btn btn-primary" aria-label="Explore services">
              Explore services
            </a>
          </div>
          <div className="cta-card">
            <h3>Prefer to talk it through?</h3>
            <p>
              Tell us a bit about your project and we’ll get back with options, timelines, and next
              steps.
            </p>
            <a href="/contact" className="btn btn-ghost" aria-label="Contact us">
              Contact us
            </a>
          </div>
        </div>
      </div>

      {/* Existing site banner */}
      <Banner />
    </section>
  );
};

export default About;
